import type { Editor as EditorType } from 'hugerte';

/**
 * HugeRte's dialogs (help, link, image, ...) are opened via `editor.windowManager.open()`
 * with no `onClose` callback, so closing one lets focus fall through to whatever's next
 * in DOM order (typically the editor's content iframe) instead of returning to the
 * toolbar button that opened it — a WCAG 2.4.3 Focus Order violation.
 *
 * Timeline (verified in the browser on the dashboard/updates flow):
 *   click ?  →  BeforeExecCommand mceFocus   (editor grabs focus off the toolbar button)
 *            →  BeforeExecCommand mceHelp    (dialog command starts)
 *            →  focusin on the dialog        (dialog UI mounts & takes focus)
 *            →  OpenWindow                   (activeElement is the dialog, not the trigger)
 *            …
 *   close    →  CloseWindow (sync, activeElement = body)
 *            →  BeforeExecCommand mceFocus   (HugeRte moves focus back to the iframe)
 *            →  rAF  (activeElement = iframe)
 *
 * Two consequences for the fix:
 * 1. `document.activeElement` at `OpenWindow` is unreliable — the dialog has already
 *    grabbed focus. We can't snapshot it and later restore it. Instead, when a
 *    dialog-opening command (`mceHelp`/`mceLink`/`mceImage`) is in flight, we resolve
 *    the trigger to the corresponding toolbar button by its translated aria-label/title.
 *    This covers all three entry points — mouse click, keyboard activation on a focused
 *    toolbar button, and shortcut (e.g. Access+0 for Help, patched in
 *    `patches/hugerte.patch`).
 * 2. HugeRte re-focuses the editor via `mceFocus` synchronously *after* our
 *    `CloseWindow` handler runs. When rendered inside a react-aria `FocusScope`
 *    (`@change/ds-core-ui` `Modal` on the dashboard/updates flow), the sibling
 *    `attachModalTabFocusFix` also re-forwards iframe focus into the editor body.
 *    A synchronous `.focus()` call is overwritten by both. We defer the focus call
 *    to the next animation frame so it lands after HugeRte's own reset — last write
 *    wins. Focusing the toolbar button (not the iframe) sidesteps
 *    `attachModalTabFocusFix` entirely.
 */

// HugeRte commands that open a dialog, mapped to the label key we hand to `editor.translate`
// to find the corresponding toolbar button in the currently rendered locale.
const COMMAND_TO_LABEL_KEY: Record<string, string> = {
	mceHelp: 'Help',
	mceLink: 'Insert/Edit link',
	mceImage: 'Insert/Edit image',
};

const findToolbarButtonByLabel = (editor: EditorType, labelKey: string): HTMLElement | null => {
	// eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- hugerte's own translate, not Corgi's i18n
	const label = editor.translate(labelKey);
	const escaped = label.replace(/["\\]/g, '\\$&');
	return editor
		.getContainer()
		.querySelector<HTMLElement>(`.tox-tbtn[aria-label="${escaped}"], .tox-tbtn[title="${escaped}"]`);
};

export const restoreFocusOnDialogClose = (editor: EditorType): void => {
	let triggerElement: HTMLElement | null = null;
	let pendingDialogCommand: string | null = null;

	const handleBeforeExecCommand = (event: { command: string }): void => {
		if (event.command in COMMAND_TO_LABEL_KEY) {
			pendingDialogCommand = event.command;
		}
	};

	const handleOpenWindow = (): void => {
		if (pendingDialogCommand) {
			const labelKey = COMMAND_TO_LABEL_KEY[pendingDialogCommand];
			triggerElement = labelKey ? findToolbarButtonByLabel(editor, labelKey) : null;
			pendingDialogCommand = null;
		} else {
			// Dialog opened outside the tracked commands (e.g. programmatic
			// `editor.windowManager.open`) — fall back to whatever had focus.
			const active = document.activeElement;
			triggerElement = active instanceof HTMLElement ? active : null;
		}
	};

	const handleCloseWindow = (): void => {
		const target = triggerElement;
		triggerElement = null;
		if (!target) return;
		requestAnimationFrame(() => {
			if (document.body.contains(target)) target.focus();
		});
	};

	editor.on('BeforeExecCommand', handleBeforeExecCommand);
	editor.on('OpenWindow', handleOpenWindow);
	editor.on('CloseWindow', handleCloseWindow);

	editor.once('remove', () => {
		editor.off('BeforeExecCommand', handleBeforeExecCommand);
		editor.off('OpenWindow', handleOpenWindow);
		editor.off('CloseWindow', handleCloseWindow);
	});
};
